import { SxProps, Theme } from "@mui/material";
import { assessmentPhoneLandscapeMedia, assessmentShortViewportMedia } from "../assessment-layout-styles";

export const cardStyle = (
  theme: Theme,
  compact: boolean,
  isExpanded = false
): SxProps<Theme> => ({
  p: compact ? 1.25 : 2.5,
  borderRadius: compact ? "16px" : "20px",
  backgroundColor: compact
    ? theme.palette.quizPopap.quizBg
    : theme.palette.quizPopap.quizInsideBg,
  border: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  boxShadow: compact ? "0 10px 30px rgba(15, 23, 42, 0.06)" : "none",
  width: "100%",
  minHeight: 0,
  ...(compact
    ? {
        [assessmentPhoneLandscapeMedia]: {
          p: 0.75,
          borderRadius: "12px",
          boxShadow: "0 4px 14px rgba(15, 23, 42, 0.05)",
        },
        [assessmentShortViewportMedia]: {
          p: 0.75,
          borderRadius: "12px",
        },
      }
    : {}),
  ...(isExpanded && compact
    ? {
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        maxHeight: "min(42dvh, 320px)",
        [assessmentPhoneLandscapeMedia]: {
          flexShrink: 0,
          width: "100%",
          height: "100%",
          minHeight: 0,
          maxHeight: "min(55dvh, 265px)",
        },
      }
    : {}),
});

export const listContainerStyle = (
  isExpanded: boolean,
  compact: boolean
): SxProps<Theme> => {
  if (!isExpanded) {
    return {};
  }

  const scrollSx: SxProps<Theme> = {
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
    pr: 0.25,
  };

  if (!compact) {
    return {
      ...scrollSx,
      maxHeight: "min(45vh, 280px)",
    };
  }

  return {
    ...scrollSx,
    maxHeight: "min(30dvh, 200px)",
    [assessmentPhoneLandscapeMedia]: {
      flex: 1,
      minHeight: 72,
      maxHeight: "none",
    },
  };
};

export const headerStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  mb: 1,
  flexShrink: 0,
  [assessmentPhoneLandscapeMedia]: {
    gap: 0.5,
    mb: 0.5,
  },
  [assessmentShortViewportMedia]: {
    gap: 0.5,
    mb: 0.5,
  },
};

export const titleStyle = (compact: boolean, theme: Theme): SxProps<Theme> => ({
  fontSize: compact ? "13px" : "16px",
  fontWeight: 800,
  mb: 0,
  color: theme.palette.quizPopap.quizText,
  ...(compact
    ? {
        [assessmentPhoneLandscapeMedia]: {
          fontSize: "11px",
        },
        [assessmentShortViewportMedia]: {
          fontSize: "11px",
        },
      }
    : {}),
});

export const liveChipStyle: SxProps<Theme> = {
  backgroundColor: "rgba(34, 197, 94, 0.12)",
  color: "#16A34A",
  fontWeight: 700,
  [assessmentPhoneLandscapeMedia]: {
    height: 20,
    fontSize: "10px",
    "& .MuiChip-label": {
      px: 0.75,
      py: 0,
    },
  },
};

export const rowStyle = (
  highlighted: boolean,
  compact: boolean,
  showDivider: boolean,
  theme: Theme
): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  py: compact ? 0.95 : 1.15,
  px: compact ? (highlighted ? 1 : 0.25) : 0,
  mx: compact ? (highlighted ? 0 : -0.25) : 0,
  borderRadius: compact && highlighted ? "12px" : 0,
  backgroundColor:
    compact && highlighted ? "rgba(6, 182, 212, 0.12)" : "transparent",
  borderBottom: showDivider ? `1px solid ${theme.palette.quizPopap.quizSeparator}` : "none",
  ...(compact
    ? {
        [assessmentPhoneLandscapeMedia]: {
          py: 0.45,
          px: highlighted ? 0.75 : 0.15,
          borderRadius: highlighted ? "8px" : 0,
        },
        [assessmentShortViewportMedia]: {
          py: 0.45,
          px: highlighted ? 0.75 : 0.15,
          borderRadius: highlighted ? "8px" : 0,
        },
      }
    : {}),
});

export const rowLeftStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  flex: 1,
  minWidth: 0,
};

export const medalWrapStyle = (compact: boolean): SxProps<Theme> => ({
  width: compact ? 24 : 28,
  height: compact ? 37 : 41,
  flexShrink: 0,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  overflow: "visible",
});

export const placeBadgeStyle = (
  place: number,
  compact: boolean,
  theme: Theme,
): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";

  return {
    width: compact ? 22 : 26,
    height: compact ? 22 : 26,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: compact ? "11px" : "13px",
    fontWeight: 800,
    color: isDark ? "#E2E8F0" : "#475569",
    backgroundColor: isDark
      ? "rgba(148, 163, 184, 0.28)"
      : "rgba(148, 163, 184, 0.22)",
    flexShrink: 0,
  };
};

export const currentUserBadgeStyle = (compact: boolean): SxProps<Theme> => ({
  width: compact ? 22 : 26,
  height: compact ? 22 : 26,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: compact ? "11px" : "13px",
  fontWeight: 800,
  color: "#FFFFFF",
  backgroundColor: "#06B6D4",
  flexShrink: 0,
  ...(compact
    ? {
        [assessmentPhoneLandscapeMedia]: {
          width: 18,
          height: 18,
          fontSize: "10px",
        },
      }
    : {}),
});

export const medalStyle = (place: number, compact: boolean): SxProps<Theme> => ({
  position: "relative",
  width: compact ? 24 : 28,
  height: compact ? 24 : 28,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: compact ? "10px" : "12px",
  fontWeight: 800,
  lineHeight: 1,
  flexShrink: 0,
  color: place === 1 ? "#7C5200" : place === 2 ? "#4B5563" : "#8A4B08",
  background:
    place === 1
      ? "linear-gradient(180deg, #FDE68A 0%, #FBBF24 100%)"
      : place === 2
        ? "linear-gradient(180deg, #E5E7EB 0%, #9CA3AF 100%)"
        : "linear-gradient(180deg, #FED7AA 0%, #FB923C 100%)",
  boxShadow:
    place === 1
      ? "0 4px 10px rgba(251, 191, 36, 0.28)"
      : place === 2
        ? "0 4px 10px rgba(148, 163, 184, 0.24)"
        : "0 4px 10px rgba(251, 146, 60, 0.24)",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: -13,
    left: 7,
    width: 6,
    height: 15,
    backgroundColor:
      place === 1 ? "#F59E0B" : place === 2 ? "#9CA3AF" : "#F97316",
    clipPath: "polygon(0 0, 100% 0, 10% 100%)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -13,
    right: 7,
    width: 6,
    height: 15,
    backgroundColor:
      place === 1 ? "#D97706" : place === 2 ? "#6B7280" : "#EA580C",
    clipPath: "polygon(0 0, 100% 0, 90% 100%)",
  },
});

export const nameStyle = (theme: Theme, compact: boolean): SxProps<Theme> => ({
  fontSize: compact ? "12px" : "14px",
  fontWeight: 600,
  color: theme.palette.quizPopap.quizText,
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  ...(compact
    ? {
        [assessmentPhoneLandscapeMedia]: {
          fontSize: "11px",
        },
      }
    : {}),
});

export const pointsStyle = (compact: boolean, theme: Theme): SxProps<Theme> => ({
  fontSize: compact ? "12px" : "14px",
  fontWeight: 700,
  color: compact ? "#06B6D4" : theme.palette.quizPopap.quizText,
  flexShrink: 0,
  ...(compact
    ? {
        [assessmentPhoneLandscapeMedia]: {
          fontSize: "11px",
        },
      }
    : {}),
});

export const pointsBonusStyle = (compact: boolean): SxProps<Theme> => ({
  ml: 0.5,
  fontSize: compact ? "10px" : "11px",
  fontWeight: 400,
  lineHeight: 1,
  ...(compact
    ? {
        [assessmentPhoneLandscapeMedia]: {
          fontSize: "9px",
        },
      }
    : {}),
});

export const emptyStyle = (theme: Theme): SxProps<Theme> => ({
  fontSize: "14px",
  color: theme.palette.quizPopap.quizText,
  opacity: 0.7,
  py: 2,
});

export const actionWrapStyle: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  pt: 1,
  flexShrink: 0,
  [assessmentPhoneLandscapeMedia]: {
    pt: 0.35,
  },
};

export const actionButtonStyle = (compact: boolean): SxProps<Theme> => ({
  textTransform: "none",
  fontWeight: 700,
  fontSize: compact ? "12px" : "14px",
  color: "#06B6D4",
  px: 0,
  minWidth: 0,
  minHeight: compact ? 28 : 36,
  "&:hover": {
    backgroundColor: "transparent",
    opacity: 0.88,
  },
  ...(compact
    ? {
        [assessmentPhoneLandscapeMedia]: {
          fontSize: "10px",
          minHeight: 22,
        },
      }
    : {}),
});
